import { InitialAuthorizationCodeGrantOutcome } from "@/entry/types/WebexInstance";
import { StructuredLog } from "@agentx/agentx-services";
import { assert, ensureError } from "@uuip/unified-ui-platform-sdk";
import { cloneToken, createWebexConfig, saveIdentityUrls } from "../Authentication/utils";
import Webex from "./webex-js-sdk";
const INITIALISE_WEBEX_LOG = "Login/InitializeWebex";
const UUIP_AUTH_TOKEN_KEY = "uuip-auth-token";
function isOAuthToken(token) {
    if (token === null || token === undefined || typeof token !== "object") {
        return false;
    }
    const candidate = token;
    return (typeof candidate.access_token === "string" &&
        typeof candidate.expires_in === "number" &&
        typeof candidate.refresh_token === "string" &&
        typeof candidate.refresh_token_expires_in === "number" &&
        typeof candidate.scope === "string");
}
async function waitForCdnAuthorizationCodeExchange(webexInstance) {
    const authorization = webexInstance.authorization;
    // The SDK can return an instance without the authorization plugin installed.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!authorization) {
        return false;
    }
    if (authorization.initialAuthorizationCodeGrantOutcome === InitialAuthorizationCodeGrantOutcome.NOT_ATTEMPTED) {
        await authorization.when?.("change:initialAuthorizationCodeGrantOutcome");
    }
    return authorization.initialAuthorizationCodeGrantOutcome === InitialAuthorizationCodeGrantOutcome.SUCCESS;
}
export async function initializeWebex(config) {
    return new Promise((resolve) => {
        try {
            StructuredLog.trace(INITIALISE_WEBEX_LOG, "Initializing Webex with provided configuration using webex.ts module");
            const webexInstance = Webex.init?.(config);
            if (!webexInstance) {
                resolve(null);
                return;
            }
            window.webexService = webexInstance;
            StructuredLog.trace(INITIALISE_WEBEX_LOG, "Webex instance initialized successfully using webex.ts module");
            if (webexInstance.once) {
                webexInstance.once("ready", () => {
                    StructuredLog.trace(INITIALISE_WEBEX_LOG, "Webex instance emitted 'ready' event from initialization");
                    resolve(webexInstance);
                });
            }
        }
        catch (error) {
            StructuredLog.error(INITIALISE_WEBEX_LOG, "Failed to initialize Webex instance - Error details:", { error });
            resolve(null);
        }
    });
}
/**
 * Gets the current Webex instance, initializes if not available
 * @returns Current Webex instance or null if initialization fails
 */
export function getWebexInstance() {
    return window.webexService || null;
}
/**
 * Initializes Webex with default configuration
 * @returns Promise that resolves with Webex instance
 */
export async function initializeWebexWithConfig({ userToken, useWebExAuth }) {
    const config = createWebexConfig({ userToken, useWebExAuth });
    return initializeWebex(config);
}
export async function exchangeCdnAuthorizationCode() {
    const webexInstance = await initializeWebexWithConfig({ userToken: null, useWebExAuth: true });
    if (!webexInstance || !(await waitForCdnAuthorizationCodeExchange(webexInstance))) {
        StructuredLog.error("Login/Entry/CdnAuthorizationCodeExchange/Failed", "Webex SDK did not complete the authorization-code exchange");
        return null;
    }
    return webexInstance;
}
function getWebexToken(webexInstance) {
    const webexToken = webexInstance.credentials.supertoken;
    return isOAuthToken(webexToken) ? webexToken : null;
}
async function setUuipTokenFromWebex(webexInstance, idBrokerUrl) {
    const webexToken = getWebexToken(webexInstance);
    if (!webexToken) {
        StructuredLog.error("Login/Entry/SetUuipToken/Failed", "Webex SDK did not return a complete authorization token");
        return false;
    }
    const token = await cloneToken(webexInstance, webexToken, idBrokerUrl);
    if (!token) {
        StructuredLog.error("Login/Entry/SetUuipToken/Failed", "Unable to clone Webex token for UUIP");
        return false;
    }
    localStorage.setItem(UUIP_AUTH_TOKEN_KEY, JSON.stringify({ token, timestamp: Date.now() }));
    StructuredLog.trace("Login/Entry/SetUuipToken/Complete");
    return true;
}
/**
 * Checks if Webex is initialized
 * @returns True if Webex is initialized
 */
export function isWebexInitialized() {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return typeof window.webexService !== "undefined" && window.webexService !== null;
}
export function clearWebexInstance() {
    window.webexService = undefined;
}
export async function getOrCreateWebexInstance({ useWebExAuth } = { useWebExAuth: false }) {
    if (isWebexInitialized()) {
        StructuredLog.trace("Login/getOrCreateWebexInstance/AlreadyInitialised");
        return window.webexService;
    }
    StructuredLog.trace("Login/getOrCreateWebexInstance/CreatingNewInstance");
    return initializeWebexWithConfig({ userToken: null, useWebExAuth });
}
export function waitForServiceCatalog(webex, timeout, serviceGroup) {
    return webex.internal.services
        .waitForCatalog(serviceGroup, timeout)
        .then(() => {
        const ciUrls = {
            identityUrl: webex.internal.services.get("identity") ?? "",
            idbrokerUrl: webex.internal.services.get("idbroker") ?? ""
        };
        assert(ciUrls.identityUrl, "Identity URL is not available in Webex instance");
        assert(ciUrls.idbrokerUrl, "ID Broker URL is not available in Webex instance");
        StructuredLog.trace("Login/LoginPage/FetchIdentityURLs/Done", "");
        saveIdentityUrls(ciUrls);
        return ciUrls.idbrokerUrl;
    })
        .catch((error) => {
        StructuredLog.error("Login/LoginPage/FetchIdentityURLs/Error", "", { error: ensureError(error) });
        return null;
    });
}
const POST_AUTH_CATALOG_TIMEOUT_MS = 20000;
export async function initWebExWithWebExAuthorization(authCode) {
    // A reload or iframe already has a CI session. Leave Webex uninitialized here so login can
    // create it from that token. Starting PKCE with no supertoken occupies window.webexService
    // and the desktop never binds the session token.
    if (!authCode && localStorage.getItem(UUIP_AUTH_TOKEN_KEY)) {
        StructuredLog.trace("Login/initWebExWithWebExAuthorization/existingSessionToken");
        return null;
    }
    const localGetWebexInstance = async (theAuthCode) => {
        if (theAuthCode) {
            return exchangeCdnAuthorizationCode();
        }
        return getOrCreateWebexInstance({ useWebExAuth: true });
    };
    const webexInstance = await localGetWebexInstance(authCode);
    if (!webexInstance) {
        StructuredLog.error("Login/initWebExWithWebExAuthorization/getWebexInstanceFailed");
        return null;
    }
    if (!getWebexToken(webexInstance)) {
        StructuredLog.trace("Login/initWebExWithWebExAuthorization/webexTokenUnavailable");
        localStorage.removeItem(UUIP_AUTH_TOKEN_KEY);
        return null;
    }
    StructuredLog.debug("Login/initWebExWithWebExAuthorization/WaitForCatalog/Start");
    const idBrokerUrl = await waitForServiceCatalog(webexInstance, POST_AUTH_CATALOG_TIMEOUT_MS, "postauth");
    if (!idBrokerUrl) {
        StructuredLog.error("Login/initWebExWithWebExAuthorization/WaitForCatalog/Failed");
        return null;
    }
    StructuredLog.debug("Login/initWebExWithWebExAuthorization/WaitForCatalog/Complete", "", {
        orgId: webexInstance.credentials.getOrgId()
    });
    const uuipTokenSet = await setUuipTokenFromWebex(webexInstance, idBrokerUrl);
    if (!uuipTokenSet) {
        StructuredLog.error("Login/initWebExWithWebExAuthorization/SetUUIPToken/Failed");
        return null;
    }
    return webexInstance;
}
